import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import { apiSlice } from "../redux/api-slice";
import { updateType, removeType, updateTag, removeTag, clearAllFilters } from '../redux/filter/filter-slice'
import { debounce, forEach } from 'lodash';
import { setCurrentPage, setPagination } from '../redux/settings/page-settings-slice';
import { LoadingSpinner } from '../common/loadingSpinner';
import { A11yDialog } from 'react-a11y-dialog';
import A11yDialogInstance from 'a11y-dialog';
import { SvgIcon } from '../../../ace/pages/calendar/components/svg-icon';
import { useMediaQuery } from 'react-responsive';

export default function Filters() {
    const dispatch = useAppDispatch();
    const { types, tags } = useAppSelector((state) => state.filterState);
    const { filterCategories, isLoading, searchResults } = useAppSelector((state) => state.searchData);
    const { searchActive, searchBreakpoint, tagsHeader, contentTypesHeader } = useAppSelector((state) => state.pageSettings);
    const filterModal = useRef<A11yDialogInstance>();
    const belowLayoutBreakpoint = useMediaQuery({ query: `(max-width: ${searchBreakpoint}px)` });
    const modalClasses = {
        container: 'dialog-container ace-cal-filters-modal',
        overlay: 'dialog-overlay',
        dialog: 'ace-cal-filters-modal-content theme-dark',
        title: 'h6-style',
        closeButton: 'modal-close ace-cal-filters-modal-close x-btn'
    };

    const updateCurrentFilter = (e, filterType: 'type' | 'tag') => {
        const value = e.target.id;

        if (e.target.checked) {
            if (filterType === 'type') {
                dispatch(updateType(value));
            } else {
                dispatch(updateTag(value));
            }
        } else {
            if (filterType === 'type') {
                dispatch(removeType(value));
            } else {
                dispatch(removeTag(value));
            }
        }


        dispatch(setCurrentPage(1));
        dispatch(setPagination(true));
    };

    const handleClearAllFilters = () => {
        dispatch(clearAllFilters());
        dispatch(setCurrentPage(1));
    };

    const updateFiltersBasedOnViewPort = () => {
        if (window.matchMedia(`(max-width: ${searchBreakpoint}px)`).matches) {
            return false;
        }
        return true;
    };

    const [isFiltersOpen, setFiltersOpen] = useState(updateFiltersBasedOnViewPort());

    const handleResize = () => {
        if (updateFiltersBasedOnViewPort() === true) {
            setFiltersOpen(true);
        } else {
            setFiltersOpen(false);
        }
    }

    useEffect(() => {
        const debouncedHandleResize = debounce(handleResize, 250);
        window.addEventListener('resize', debouncedHandleResize);

        return () => {
            window.removeEventListener('resize', debouncedHandleResize);
            debouncedHandleResize.cancel();
        };
    }, [searchBreakpoint]);

    if (!searchActive) {
        return null;
    }

    // Don't render if no filter categories are available at all
    if (!filterCategories || 
        (!filterCategories.contentTypes?.length && !filterCategories.tags?.length) || 
        searchResults.length === 0) {
        return null;
    }


    return (

        <div className="search-filters-container">
            {!belowLayoutBreakpoint && (
                <div className="search-filters-header flex-between">
                    <h3 className="h5-style">Filters</h3>
                    {(types.length > 0 || tags.length > 0) && (
                        <button
                            type="button"
                            className="un-button clear-filters-btn hyperlink"
                            onClick={handleClearAllFilters}>
                            Clear All
                        </button>
                    )}
                </div>
            )}

            {belowLayoutBreakpoint && (
                <button
                    type="button"
                    className="btn-secondary btn-full-width-sm search-filters-trigger"
                    onClick={() => filterModal.current?.show()}>
                    <span>Filters</span>
                    <SvgIcon iconId={"icon-filter"} cssClass={"svg-icon svg-icon-text"} />
                </button>
            )}

            {filterCategories && (
                <>
                    {!belowLayoutBreakpoint && (
                        <div className="search-filters space-top-md">
                            {/* Content Types */}
                            {filterCategories.contentTypes && filterCategories.contentTypes.length > 0 && (
                                <details className="search-filters-group space-top-md" open>
                                    <summary className="search-filters-summary">
                                        <h4 className="h6-style">{contentTypesHeader}</h4>
                                    </summary>
                                    <ul className="search-filters-list stack-sm">
                                        {filterCategories.contentTypes.map(type => (
                                            <li key={type}>
                                                <input
                                                    type="checkbox"
                                                    name={type}
                                                    id={type}
                                                    checked={types.includes(type)}
                                                    onChange={(e) => updateCurrentFilter(e, 'type')}
                                                />

                                                <label htmlFor={type} className="checkbox-label">
                                                    {type}
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                </details>
                            )}

                            {/* Tags */}
                            {filterCategories.tags && filterCategories.tags.length > 0 && (
                                <details className="search-filters-group space-top-md" open>
                                    <summary className="search-filters-summary">
                                        <h4 className="h6-style">{tagsHeader}</h4>
                                    </summary>
                                    <ul className="search-filters-list stack-sm">
                                        {filterCategories.tags.map(tag => (
                                            <li key={tag}>
                                                <input
                                                    type="checkbox"
                                                    id={tag}
                                                    name={tag}
                                                    checked={tags.includes(tag)}
                                                    onChange={(e) => updateCurrentFilter(e, 'tag')}
                                                />

                                                <label htmlFor={tag} className="checkbox-label">
                                                    {tag}
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                        </div>
                    )}

                    {belowLayoutBreakpoint && (
                        <A11yDialog
                            id="filters_modal"
                            title={"Filters"}
                            dialogRef={instance => (filterModal.current = (instance as A11yDialogInstance))}
                            classNames={modalClasses}
                            closeButtonLabel={"Close"}>

                            <div className="search-modal-filters">
                                {/* Content Types */}
                                {filterCategories.contentTypes && filterCategories.contentTypes.length > 0 && (
                                    <details className="search-filters-group space-top-md" open>
                                        <summary className="search-filters-summary">
                                            <h4 className="h6-style">Content Types</h4>
                                        </summary>
                                        <ul className="search-filters-list stack-sm">
                                            {filterCategories.contentTypes.map(type => (
                                                <li key={type}>
                                                    <input
                                                        type="checkbox"
                                                        id={type}
                                                        name={type}
                                                        checked={types.includes(type)}
                                                        onChange={(e) => updateCurrentFilter(e, 'type')}
                                                    />

                                                    <label htmlFor={type} className="checkbox-label">
                                                        {type}
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    </details>
                                )}

                                {/* Tags */}
                                {filterCategories.tags && filterCategories.tags.length > 0 && (
                                    <details className="search-filters-group space-top-md" open>
                                        <summary className="search-filters-summary">
                                            <h4 className="h6-style">Tags</h4>
                                        </summary>
                                        <ul className="search-filters-list stack-sm">
                                            {filterCategories.tags.map(tag => (
                                                <li key={tag}>
                                                    <input
                                                        type="checkbox"
                                                        id={tag}
                                                        name={tag}
                                                        checked={tags.includes(tag)}
                                                        onChange={(e) => updateCurrentFilter(e, 'tag')}
                                                    />

                                                    <label htmlFor={tag} className="checkbox-label">
                                                        {tag}
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                            </div>
                            <div className="space-top-lg stack-md">
                               
                                    <button
                                        type="button"
                                        className="btn-primary btn-full-width"
                                        data-a11y-dialog-hide>
                                        Close
                                    </button>
                               
                                {(types.length > 0 || tags.length > 0) && (
                                    <button
                                        type="button"
                                        className="btn-secondary btn-full-width"
                                        onClick={handleClearAllFilters}>
                                        Clear All Filters
                                    </button>
                                )}
                            </div>
                        </A11yDialog>
                    )}
                </>
            )}
        </div>

    );
}





